import { type FC, useMemo } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import './CreatorChatFlowReadyCta.css';

type CreatorChatFlowReadyCtaProps = {
	hasStartedCallBefore?: boolean;
	isStartingCall?: boolean;
	onStartCall?: () => void;
};

const CreatorChatFlowReadyCta: FC<CreatorChatFlowReadyCtaProps> = ({
	hasStartedCallBefore = false,
	isStartingCall = false,
	onStartCall
}) => {
	const { localization } = useHostContext();
	const buttonLabel = useMemo(() => {
		if (isStartingCall) {
			return localization.rich(
				'creator',
				'chatFlow.readyCta.button.callingLabel',
				{
					br: () => <br />
				}
			);
		}

		if (hasStartedCallBefore) {
			return localization.rich(
				'creator',
				'chatFlow.readyCta.button.resumeLabel',
				{
					br: () => <br />
				}
			);
		}

		return localization.rich('creator', 'chatFlow.readyCta.button.startLabel', {
			br: () => <br />
		});
	}, [hasStartedCallBefore, isStartingCall, localization]);

	return (
		<div
			className="relative z-30 flex flex-col items-center pointer-events-none"
			data-testid="creator-chat-flow-ready-cta"
		>
			<div
				className="creator-chat-flow-ready-cta__callout absolute -left-[212px] -top-[118px] flex flex-col items-center text-center text-[28px] leading-[0.95] text-primary-900/65"
				style={{ fontFamily: '"HanziPen SC", cursive' }}
			>
				<span>
					{localization.translate(
						'creator',
						'chatFlow.readyCta.calloutLineOne'
					)}
				</span>
				<span>
					{localization.translate(
						'creator',
						'chatFlow.readyCta.calloutLineTwo'
					)}
				</span>
				<svg
					aria-hidden="true"
					className="pointer-events-none absolute left-[126px] top-[58px] h-[104px] w-[188px] overflow-visible"
					fill="none"
					viewBox="0 0 188 104"
				>
					<path
						className="creator-chat-flow-ready-cta__arrow-dash"
						d="M10 10C42 18 68 28 92 46C116 64 136 78 160 82"
						stroke="currentColor"
						strokeDasharray="1 12"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="3"
					/>
					<path
						className="creator-chat-flow-ready-cta__arrow-head"
						d="M148 72L160 82L144 86"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="3"
					/>
				</svg>
			</div>

			<div className="relative flex h-[320px] w-[320px] items-center justify-center rounded-full pointer-events-auto">
				<span className="creator-chat-flow-ready-cta__core-glow absolute h-[246px] w-[246px] rounded-full bg-primary-900/[0.08] blur-[2px]" />
				<span className="creator-chat-flow-ready-cta__wave absolute h-[236px] w-[236px] rounded-full border border-primary-900/20" />
				<span
					className="creator-chat-flow-ready-cta__wave absolute h-[236px] w-[236px] rounded-full border border-primary-900/16"
					style={{ animationDelay: '0.85s' }}
				/>
				<span
					className="creator-chat-flow-ready-cta__wave absolute h-[236px] w-[236px] rounded-full border border-primary-900/12"
					style={{ animationDelay: '1.7s' }}
				/>
				<button
					className="creator-chat-flow-ready-cta__button relative z-20 flex h-[236px] w-[236px] cursor-pointer items-center justify-center rounded-full bg-primary-900 text-center text-[58px] font-semibold leading-[0.95] text-white shadow-[0_18px_44px_rgba(43,127,255,0.2)] transition-opacity duration-200 will-change-transform [font-family:var(--font-headlines)] disabled:cursor-not-allowed disabled:opacity-70"
					data-testid="creator-chat-flow-start-call-button"
					disabled={isStartingCall}
					onClick={onStartCall}
					type="button"
				>
					<span>{buttonLabel}</span>
				</button>
			</div>
		</div>
	);
};

export default CreatorChatFlowReadyCta;
